import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { Check, Construction, Link2, Sparkles } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PremiumSelect } from '@/components/ui/premium-select';
import { cn } from '@/lib/utils';
import { useLanguage } from '../../context/LanguageContext';
import {
  MOBILITY_OPTIONS,
  MOBILITY_WHEELED,
  MOBILITY_TRACKED,
  MACHINE_TYPES_BY_MOBILITY,
  isRtoApplicable,
  ATTACHMENT_OPTIONS,
  RATE_FIELDS,
  ATTACHMENT_RATE_ELIGIBLE,
} from '../../utils/machineRegistrationConfig';

const MOBILITY_ICONS = {
  wheeled: Construction,
  tracked: Link2,
};

const MOBILITY_I18N = {
  wheeled: {
    title: 'regMobilityWheeled',
    hint: 'regMobilityWheeledHint',
    tag: 'regTagRto',
  },
  tracked: {
    title: 'regMobilityTracked',
    hint: 'regMobilityTrackedHint',
    tag: 'regTagNoRto',
  },
};

const OWNERSHIP_OPTIONS = [
  { value: 'new', labelKey: 'machineOwnershipNew' },
  { value: 'used_first_owner', labelKey: 'machineOwnershipUsed' },
  { value: 'second_hand', labelKey: 'machineOwnershipSecondHand' },
];

function suggestFuelConsumptionLph(machineType, capacity) {
  const type = String(machineType || '').toLowerCase();
  const cap = String(capacity || '').toLowerCase();
  if (type.includes('backhoe')) return 3.5;
  if (type.includes('excavator') || type.includes('poclain')) {
    const tonMatch = cap.match(/(\d+(?:\.\d+)?)\s*t/);
    if (tonMatch && Number(tonMatch[1]) >= 20) return 15;
    return 12;
  }
  return null;
}

function MobilityCard({ selected, onClick, icon: Icon, title, description, tag }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'relative w-full rounded-2xl border p-4 text-left transition-all duration-200',
        'backdrop-blur-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C]/50',
        selected
          ? 'border-[#C9A84C] bg-gradient-to-br from-[#C9A84C]/14 to-[#C9A84C]/5 shadow-[0_0_28px_rgba(201,168,76,0.18)]'
          : 'border-white/10 bg-white/[0.04] hover:border-[#C9A84C]/40 hover:bg-white/[0.07] hover:-translate-y-0.5',
      )}
    >
      {selected && (
        <span className="absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-full bg-[#C9A84C] text-[#0D1B2A]">
          <Check className="h-3.5 w-3.5" strokeWidth={3} />
        </span>
      )}
      <div className="flex items-start gap-3 pr-6">
        <span
          className={cn(
            'flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border',
            selected
              ? 'border-[#C9A84C]/55 bg-[#C9A84C]/18 text-[#E8D5A3]'
              : 'border-white/12 bg-white/[0.05] text-[#C9A84C]/85',
          )}
        >
          <Icon className="h-5 w-5" strokeWidth={1.75} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-white">{title}</p>
          {tag && (
            <span className="mt-1.5 inline-block rounded-full border border-[#C9A84C]/25 bg-[#C9A84C]/10 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-[#E8D5A3]">
              {tag}
            </span>
          )}
          <p className="mt-2 text-xs leading-relaxed text-white/55">{description}</p>
        </div>
      </div>
    </button>
  );
}

const MachineRegistrationStep2 = forwardRef(function MachineRegistrationStep2({
  regData,
  setRegData,
  setRegDocuments,
  setRegError,
  emptyRegDocumentsForMobility,
  isMobile,
}, ref) {
  const { t } = useLanguage();
  const [loanSectionOpen, setLoanSectionOpen] = React.useState(false);
  const [paymentTermsOpen, setPaymentTermsOpen] = React.useState(true);
  const [regSection, setRegSection] = React.useState('machine'); // 'machine' | 'rates' | 'purchase'
  const [openCards, setOpenCards] = useState(['machine']);
  const [fieldsExpanded, setFieldsExpanded] = useState(true);

  // Phase 10 defaults — ensure submit payload always includes payment terms.
  React.useEffect(() => {
    setRegData((prev) => {
      if (
        prev.payment_due_days != null
        && prev.early_payment_days != null
        && prev.early_payment_discount_pct != null
        && prev.late_payment_fee_pct != null
      ) {
        return prev;
      }
      return {
        ...prev,
        payment_due_days: prev.payment_due_days ?? 15,
        early_payment_days: prev.early_payment_days ?? 3,
        early_payment_discount_pct: prev.early_payment_discount_pct ?? 0,
        late_payment_fee_pct: prev.late_payment_fee_pct ?? 0,
      };
    });
  }, [setRegData]);

  const SECTION_ORDER = ['machine', 'rates', 'purchase'];
  const SECTION_LABELS = {
    machine: 'Machine Info',
    rates: 'Attachments + Rates',
    purchase: 'Purchase Details',
  };
  const sectionIndex = SECTION_ORDER.indexOf(regSection);

  const goSectionNext = () => {
    if (sectionIndex < SECTION_ORDER.length - 1) {
      setRegSection(SECTION_ORDER[sectionIndex + 1]);
    }
  };
  const goSectionBack = () => {
    if (sectionIndex > 0) {
      setRegSection(SECTION_ORDER[sectionIndex - 1]);
    }
  };

  const toggleCard = (key) => {
    if (!isMobile) return;
    setOpenCards((prev) =>
      prev.includes(key)
        ? prev.length > 1 ? prev.filter((k) => k !== key) : prev
        : [...prev, key]
    );
  };
  const isCardOpen = (key) => !isMobile || openCards.includes(key);

  useImperativeHandle(ref, () => ({
    goSectionNext,
    goSectionBack,
    currentSection: regSection,
  }), [regSection, sectionIndex]);

  const applyFuelConsumptionDefault = (machineType, capacity) => {
    const suggested = suggestFuelConsumptionLph(machineType, capacity);
    if (suggested == null) return {};
    return { fuel_consumption_liters_per_hour: suggested };
  };

  const handleMobilitySelect = (optId) => {
    setRegData((prev) => ({
      ...prev,
      mobilityClass: optId,
      machineType: '',
      regNo: optId === MOBILITY_TRACKED ? '' : prev.regNo,
      pollutionValid: optId === MOBILITY_TRACKED ? false : prev.pollutionValid,
    }));
    setRegDocuments(emptyRegDocumentsForMobility(optId));
    setRegError('');
    setFieldsExpanded(true);
  };

  const machineTypes = MACHINE_TYPES_BY_MOBILITY[regData.mobilityClass] || [];
  const showFields = Boolean(regData.mobilityClass);

  const premiumCardStyle = {
    background: '#0f1f38',
    border: '1px solid rgba(201,168,76,0.2)',
    borderRadius: '10px',
    padding: '18px',
    marginBottom: '14px',
  };
  const premiumCardTitleStyle = {
    fontSize: '11px',
    fontWeight: 600,
    color: '#C9A84C',
    letterSpacing: '.08em',
    textTransform: 'uppercase',
    marginBottom: '16px',
    paddingBottom: '10px',
    borderBottom: '1px solid #1e3a5f',
  };
  const sectionDividerStyle = {
    fontSize: '11px',
    color: '#4a6080',
    marginBottom: '8px',
    paddingBottom: '6px',
    borderBottom: '1px solid #1e3a5f',
  };

  return (
    <div className="space-y-4">
      <div>
        <div className="mb-1 flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-[#C9A84C]" />
          <h4 className="de-text-gradient m-0 text-base font-bold">{t('regMachineInfoTitle')}</h4>
        </div>
        <p className="m-0 max-w-xl text-xs leading-relaxed text-white/50">
          {t('regMachineIntro')}
        </p>
      </div>

      <div style={premiumCardStyle}>
        <div
          onClick={() => toggleCard('machine')}
          style={{ cursor: isMobile ? 'pointer' : 'default', display: 'flex',
            justifyContent: 'space-between', alignItems: 'center',
            marginBottom: isCardOpen('machine') ? 0 : 0 }}
        >
          <p style={{ ...premiumCardTitleStyle, marginBottom: 0, borderBottom: 'none' }}>
            Machine Details
          </p>
          {isMobile && (
            <span style={{ color: '#C9A84C', fontSize: 12 }}>
              {isCardOpen('machine') ? '▼' : '▶'}
            </span>
          )}
        </div>
        {isCardOpen('machine') && (
          <div style={{ borderTop: '1px solid #1e3a5f', marginTop: 10, paddingTop: 10 }}>
        <div>
          <Label className="mb-2 block normal-case text-white/70">
            {t('regMachineCategory')}
          </Label>
          <div
            className={cn(
              'grid gap-3',
              isMobile ? 'grid-cols-1' : 'grid-cols-2',
            )}
          >
            {MOBILITY_OPTIONS.map((opt) => {
              const Icon = MOBILITY_ICONS[opt.id] || Construction;
              const selected = regData.mobilityClass === opt.id;
              const i18n = MOBILITY_I18N[opt.id] || MOBILITY_I18N.wheeled;
              return (
                <MobilityCard
                  key={opt.id}
                  selected={selected}
                  onClick={() => handleMobilitySelect(opt.id)}
                  icon={Icon}
                  title={t(i18n.title)}
                  description={t(i18n.hint)}
                  tag={t(i18n.tag)}
                />
              );
            })}
          </div>
        </div>

      {showFields && (
        <>
          <div
            onClick={() => isMobile && setFieldsExpanded((v) => !v)}
            style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              cursor: isMobile ? 'pointer' : 'default',
              marginTop: 12, marginBottom: fieldsExpanded ? 0 : 0,
              padding: '6px 0',
              borderTop: '1px solid #1e3a5f',
            }}
          >
            <span style={{ fontSize: 11, color: '#C9A84C', fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase' }}>
              Machine Details
            </span>
            {isMobile && (
              <span style={{ color: '#C9A84C', fontSize: 12 }}>
                {fieldsExpanded ? '▼' : '▶'}
              </span>
            )}
          </div>
          {fieldsExpanded && (
          <>
          <div className="relative z-[1] mt-4">
            <Label className="mb-2 block normal-case text-white/70">
              {t('regEquipmentCategory')}
            </Label>
            <PremiumSelect
              value={regData.machineType}
              onValueChange={(machineType) => setRegData((prev) => ({
                ...prev,
                machineType,
                ...applyFuelConsumptionDefault(machineType, prev.capacity),
              }))}
              options={machineTypes}
              placeholder={t('regSelectType')}
            />
          </div>

          {[
            {
              label: t('regMachineNameModel'),
              key: 'machineName',
              placeholder:
                regData.mobilityClass === MOBILITY_WHEELED
                  ? 'JCB 3DX Backhoe Loader'
                  : 'Tata Hitachi EX 200',
            },
            ...(isRtoApplicable(regData.mobilityClass)
              ? [
                  {
                    label: t('regRtoNumber'),
                    key: 'regNo',
                    placeholder: 'MH-09-XX-1234',
                  },
                ]
              : [
                  {
                    label: t('regFleetIdOptional'),
                    key: 'regNo',
                    placeholder: 'Internal fleet no. or serial — no RTO',
                  },
                ]),
            {
              label: t('regYearManufacture'),
              key: 'year',
              placeholder: '2022',
            },
            {
              label: t('regCapacitySpecs'),
              key: 'capacity',
              placeholder: 'e.g. 20T / 0.8 m³ bucket',
            },
          ].map((field) => (
            <div key={field.key}>
              <Label htmlFor={`reg-${field.key}`} className="mb-2 block normal-case text-white/70">
                {field.label}
              </Label>
              <Input
                id={`reg-${field.key}`}
                placeholder={field.placeholder}
                value={regData[field.key] || ''}
                onChange={(e) => setRegData((prev) => {
                  const next = { ...prev, [field.key]: e.target.value };
                  if (field.key === 'capacity') {
                    return {
                      ...next,
                      ...applyFuelConsumptionDefault(prev.machineType, e.target.value),
                    };
                  }
                  return next;
                })}
              />
            </div>
          ))}

          <div>
            <Label htmlFor="reg-pincode" className="mb-2 block normal-case text-white/70">
              Machine Base Location Pincode
              <span className="ml-1 text-red-400">*</span>
            </Label>
            <Input
              id="reg-pincode"
              type="text"
              inputMode="numeric"
              autoComplete="postal-code"
              maxLength={6}
              placeholder="415110"
              value={regData.pincode || ''}
              onChange={(e) => {
                const digits = String(e.target.value || '').replace(/\D/g, '').slice(0, 6);
                setRegData((prev) => ({ ...prev, pincode: digits }));
              }}
            />
            <p className="mt-1 text-xs text-white/45">
              6-digit pincode of the machine&apos;s usual base / garage
            </p>
          </div>

          <div className="mb-3">
            <Label className="mb-1 block normal-case text-white/70">
              Fuel Tank Capacity (Litres)
              <span className="text-xs text-white/50 ml-2">
                optional
              </span>
            </Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min="0"
                max="1000"
                placeholder="e.g. 120"
                className="w-32"
                value={regData.fuel_capacity_litres ?? ''}
                onChange={(e) => setRegData((prev) => ({
                  ...prev,
                  fuel_capacity_litres: e.target.value
                    ? Number(e.target.value) : null,
                }))}
              />
              <span className="text-xs text-white/50">
                litres
              </span>
            </div>
          </div>
          </>
          )}
        </>
      )}
          </div>
        )}
      </div>

      {showFields && (
        <>
      <div style={premiumCardStyle}>
        <div
          onClick={() => toggleCard('rates')}
          style={{ cursor: isMobile ? 'pointer' : 'default', display: 'flex',
            justifyContent: 'space-between', alignItems: 'center',
            marginBottom: isCardOpen('rates') ? 0 : 0 }}
        >
          <p style={{ ...premiumCardTitleStyle, marginBottom: 0, borderBottom: 'none' }}>
            Attachments &amp; Rates
          </p>
          {isMobile && (
            <span style={{ color: '#C9A84C', fontSize: 12 }}>
              {isCardOpen('rates') ? '▼' : '▶'}
            </span>
          )}
        </div>
        {isCardOpen('rates') && (
          <div style={{ borderTop: '1px solid #1e3a5f', marginTop: 10, paddingTop: 10 }}>
          {/* Attachments — optional, additive */}
          <div>
            <p className="text-sm font-medium mb-2 text-white/80" style={sectionDividerStyle}>
              Available Attachments <span className="opacity-50">(optional)</span>
            </p>
            <div className="grid grid-cols-2 gap-2">
              {ATTACHMENT_OPTIONS.map((opt) => (
                <label key={opt.id} className="flex items-center gap-2 text-sm cursor-pointer text-white/75">
                  <input
                    type="checkbox"
                    value={opt.id}
                    checked={(regData.attachments || []).includes(opt.id)}
                    onChange={(e) => {
                      const current = regData.attachments || [];
                      const updated = e.target.checked
                        ? [...current, opt.id]
                        : current.filter((a) => a !== opt.id);
                      setRegData((prev) => {
                        const nextRates = { ...(prev.attachment_rates || {}) };
                        const nextMins = { ...(prev.attachment_min_hours || {}) };
                        if (!e.target.checked) {
                          delete nextRates[opt.id];
                          delete nextMins[opt.id];
                        }
                        return {
                          ...prev,
                          attachments: updated,
                          attachment_rates: nextRates,
                          attachment_min_hours: nextMins,
                        };
                      });
                    }}
                  />
                  {opt.label}
                </label>
              ))}
            </div>

            {(regData.attachments || []).includes('other') && (
              <Input
                type="text"
                placeholder="Attachment details..."
                className="mt-2"
                value={regData.attachment_notes || ''}
                onChange={(e) => setRegData((prev) => ({ ...prev, attachment_notes: e.target.value }))}
              />
            )}

            {(regData.attachments || []).length > 1 && (
              <div className="mt-3">
                <Label className="mb-1 block normal-case text-white/70">
                  Primary attachment (default)
                </Label>
                <select
                  className="w-full rounded-md border border-white/15 bg-white/[0.06] px-3 py-2 text-sm text-white"
                  value={regData.primary_attachment || ''}
                  onChange={(e) => setRegData((prev) => ({ ...prev, primary_attachment: e.target.value }))}
                >
                  <option value="">Select primary</option>
                  {(regData.attachments || []).map((id) => (
                    <option key={id} value={id}>
                      {ATTACHMENT_OPTIONS.find((o) => o.id === id)?.label || id}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Rate Chart — owner sets their own rates */}
          <div className="mt-6 pt-4">
            <p className="text-sm font-semibold mb-1 text-white/90" style={sectionDividerStyle}>Rate Chart</p>
            <p className="text-xs text-white/50 mb-3">
              You decide your rates. Platform charges 15% commission on each booking.
            </p>

            <div className="mb-3">
              <Label className="mb-1 block normal-case text-white/70">
                Minimum Hours *
                <span className="text-xs text-white/50 ml-2">
                  (Owner sets minimum booking duration)
                </span>
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="1"
                  max="24"
                  placeholder="e.g. 8"
                  className="w-32"
                  value={regData.minimum_hours ?? ''}
                  onChange={(e) => setRegData((prev) => ({
                    ...prev,
                    minimum_hours: e.target.value
                      ? Number(e.target.value) : null,
                  }))}
                />
                <span className="text-xs text-white/50">
                  hours minimum per booking
                </span>
              </div>
            </div>

            {/* Diesel basis */}
            <div className="mb-3">
              <Label className="mb-1 block normal-case text-white/70">
                Diesel / Fuel arrangement *
              </Label>
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                {[
                  { value: 'owner', label: 'Owner supplies', sub: 'Use fuel-included rate' },
                  { value: 'client', label: 'Client arranges', sub: 'Use fuel-excluded rate' },
                  { value: 'negotiable', label: 'Negotiable', sub: 'Client selects at booking' },
                ].map((opt) => (
                  <div
                    key={opt.value}
                    onClick={() => setRegData((prev) => ({ ...prev, fuel_basis: opt.value }))}
                    style={{
                      flex: 1, padding: '10px 8px', borderRadius: 8, textAlign: 'center',
                      cursor: 'pointer', fontSize: 12,
                      border: (regData.fuel_basis ?? 'negotiable') === opt.value
                        ? '1px solid rgba(201,168,76,0.5)'
                        : '1px solid rgba(255,255,255,0.1)',
                      background: (regData.fuel_basis ?? 'negotiable') === opt.value
                        ? 'rgba(201,168,76,0.08)' : 'transparent',
                      color: (regData.fuel_basis ?? 'negotiable') === opt.value
                        ? '#C9A84C' : 'rgba(255,255,255,0.5)',
                    }}
                  >
                    <div style={{ fontWeight: 500 }}>{opt.label}</div>
                    <div style={{ fontSize: 10, marginTop: 2, opacity: 0.7 }}>{opt.sub}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Hourly rate — fuel included */}
            <div className="mb-3">
              <Label className="mb-1 block normal-case text-white/70">
                Hourly rate — diesel included (₹) *
              </Label>
              <Input
                type="number"
                min="0"
                placeholder="e.g. 1200"
                value={regData.rate_per_hour_fuel_included ?? ''}
                onChange={(e) => setRegData((prev) => ({
                  ...prev,
                  rate_per_hour_fuel_included: e.target.value ? Number(e.target.value) : null,
                  rate_per_hour: e.target.value ? Number(e.target.value) : prev.rate_per_hour,
                }))}
              />
            </div>
            {/* Hourly rate — fuel excluded */}
            <div className="mb-3">
              <Label className="mb-1 block normal-case text-white/70">
                Hourly rate — diesel excluded (₹) *
              </Label>
              <Input
                type="number"
                min="0"
                placeholder="e.g. 600"
                value={regData.rate_per_hour_fuel_excluded ?? ''}
                onChange={(e) => setRegData((prev) => ({
                  ...prev,
                  rate_per_hour_fuel_excluded: e.target.value ? Number(e.target.value) : null,
                }))}
              />
              <p className="text-xs text-white/40 mt-1">
                rate_per_hour (legacy) auto-set from fuel-included rate above
              </p>
            </div>

            {RATE_FIELDS.map((field) => (
              <div key={field.key} className="mb-3">
                <Label className="mb-1 block normal-case text-white/70">
                  {field.label}
                  {field.required && <span className="text-red-400 ml-1">*</span>}
                </Label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-white/60">₹</span>
                  <Input
                    type="number"
                    min="0"
                    placeholder={field.required ? 'Required' : 'Optional'}
                    className="flex-1"
                    value={regData[field.key] ?? ''}
                    onChange={(e) => setRegData((prev) => ({
                      ...prev,
                      [field.key]: e.target.value ? Number(e.target.value) : null,
                    }))}
                  />
                  <span className="text-xs text-white/50 whitespace-nowrap">{field.suffix}</span>
                </div>
                {field.hint && (
                  <p className="text-xs text-white/45 mt-1">{field.hint}</p>
                )}
              </div>
            ))}

            {/* Operator policy */}
            <div className="mb-3 mt-4">
              <p className="text-sm font-semibold mb-2 text-white/90" style={sectionDividerStyle}>
                Operator
              </p>
              <div
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 8, padding: '10px 14px', marginBottom: 10, cursor: 'pointer',
                }}
                onClick={() => setRegData((prev) => ({
                  ...prev, operator_included: !(prev.operator_included ?? true),
                }))}
              >
                <div>
                  <div style={{ fontSize: 13, color: '#e8e0d0' }}>Operator included in rate</div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>
                    {(regData.operator_included ?? true)
                      ? 'Client does not arrange operator separately'
                      : 'Client may need to arrange operator'}
                  </div>
                </div>
                <div style={{
                  width: 36, height: 20, borderRadius: 10, position: 'relative',
                  background: (regData.operator_included ?? true)
                    ? 'rgba(201,168,76,0.4)' : 'rgba(255,255,255,0.1)',
                  flexShrink: 0,
                }}>
                  <div style={{
                    position: 'absolute', width: 14, height: 14, borderRadius: '50%',
                    top: 3, transition: '0.2s',
                    left: (regData.operator_included ?? true) ? 19 : 3,
                    background: (regData.operator_included ?? true) ? '#C9A84C' : '#8896a8',
                  }} />
                </div>
              </div>
              {!(regData.operator_included ?? true) && (
                <div className="mb-3">
                  <Label className="mb-1 block normal-case text-white/70">
                    Operator daily rate (₹) — shown to client as add-on
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    placeholder="e.g. 1200"
                    value={regData.operator_daily_rate ?? ''}
                    onChange={(e) => setRegData((prev) => ({
                      ...prev,
                      operator_daily_rate: e.target.value ? Number(e.target.value) : null,
                    }))}
                  />
                </div>
              )}
            </div>

            {(regData.attachments || []).some((a) => ATTACHMENT_RATE_ELIGIBLE.includes(a)) && (
              <div className="mt-4">
                <p className="text-sm font-medium mb-1 text-white/80">Attachment Extra Rates</p>
                <p className="text-xs text-white/50 mb-3">
                  Extra per hour charged when this attachment is in use (over base hourly rate)
                </p>
                {(regData.attachments || [])
                  .filter((a) => ATTACHMENT_RATE_ELIGIBLE.includes(a))
                  .map((attachId) => {
                    const label = ATTACHMENT_OPTIONS.find((o) => o.id === attachId)?.label || attachId;
                    return (
                      <div key={attachId} className="mb-3">
                        <Label className="mb-1 block normal-case text-white/70">
                          {label} — extra rate
                        </Label>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-white/60">₹</span>
                          <Input
                            type="number"
                            min="0"
                            placeholder="Optional"
                            className="flex-1"
                            value={(regData.attachment_rates || {})[attachId] ?? ''}
                            onChange={(e) => {
                              const nextVal = e.target.value ? Number(e.target.value) : undefined;
                              setRegData((prev) => {
                                const nextRates = { ...(prev.attachment_rates || {}) };
                                const nextMins = { ...(prev.attachment_min_hours || {}) };
                                if (nextVal === undefined) {
                                  delete nextRates[attachId];
                                  delete nextMins[attachId];
                                } else {
                                  nextRates[attachId] = nextVal;
                                }
                                return {
                                  ...prev,
                                  attachment_rates: nextRates,
                                  attachment_min_hours: nextMins,
                                };
                              });
                            }}
                          />
                          <span className="text-xs text-white/50 whitespace-nowrap">/ hour extra</span>
                        </div>
                        <Label className="mb-1 mt-2 block normal-case text-white/70">
                          Minimum usage hours for this attachment
                        </Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="Optional"
                            className="flex-1"
                            value={(regData.attachment_min_hours || {})[attachId] ?? ''}
                            onChange={(e) => {
                              const nextVal = e.target.value ? Number(e.target.value) : undefined;
                              setRegData((prev) => {
                                const nextMins = { ...(prev.attachment_min_hours || {}) };
                                if (nextVal === undefined) delete nextMins[attachId];
                                else nextMins[attachId] = nextVal;
                                return { ...prev, attachment_min_hours: nextMins };
                              });
                            }}
                          />
                          <span className="text-xs text-white/50 whitespace-nowrap">hours min</span>
                        </div>
                        <p className="mt-1 text-xs text-white/45">
                          Shortest attachment usage you will accept when this attachment is needed. Leave blank for no restriction.
                        </p>
                      </div>
                    );
                  })}
              </div>
            )}

            <div className="mt-4 border-t border-white/10 pt-4">
              <Label className="mb-1 flex cursor-pointer items-center gap-2 normal-case text-white/70">
                <input
                  type="checkbox"
                  checked={Boolean(regData.is_negotiable)}
                  onChange={(e) => setRegData((prev) => ({
                    ...prev,
                    is_negotiable: e.target.checked,
                    ...(e.target.checked ? {} : { minimum_rate_per_cycle: null }),
                  }))}
                  className="h-4 w-4 accent-[#C9A84C]"
                />
                Rates are negotiable for contract / custom projects
              </Label>
              <p className="mb-3 text-xs text-white/45">
                Optional. When enabled, you must set a minimum floor per billing cycle — quotations cannot go below it.
              </p>
              {Boolean(regData.is_negotiable) && (
                <div className="mb-3">
                  <Label className="mb-1 block normal-case text-white/70">
                    Minimum rate per cycle
                    <span className="ml-1 text-red-400">*</span>
                  </Label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-white/60">₹</span>
                    <Input
                      type="number"
                      min="0"
                      placeholder="Required when negotiable"
                      className="flex-1"
                      value={regData.minimum_rate_per_cycle ?? ''}
                      onChange={(e) => setRegData((prev) => ({
                        ...prev,
                        minimum_rate_per_cycle: e.target.value
                          ? Number(e.target.value)
                          : null,
                      }))}
                    />
                    <span className="whitespace-nowrap text-xs text-white/50">/ cycle</span>
                  </div>
                  <p className="mt-1 text-xs text-white/45">
                    Owner floor for monthly / quarterly / custom project quotes (₹ per billing cycle)
                  </p>
                </div>
              )}
            </div>

            {regData.rate_per_hour > 0 && (
              <div className="mt-4 rounded-lg border border-white/10 bg-white/[0.04] p-3 text-sm text-white/80">
                <p className="font-medium mb-1 text-[#E8D5A3]">Rate Preview</p>
                <p>Base: ₹{Number(regData.rate_per_hour).toLocaleString('en-IN')} / hr</p>
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>
                  Diesel included: ₹{Number(regData.rate_per_hour_fuel_included || 0).toLocaleString('en-IN')}/hr
                  {' · '}
                  Diesel excluded: ₹{Number(regData.rate_per_hour_fuel_excluded || 0).toLocaleString('en-IN')}/hr
                </p>
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>
                  Operator: {(regData.operator_included ?? true) ? 'Included ✓' : `Not included — ₹${Number(regData.operator_daily_rate || 0).toLocaleString('en-IN')}/day add-on`}
                </p>
                {Object.entries(regData.attachment_rates || {}).map(([id, rate]) => (
                  rate ? (
                    <p key={id} className="text-xs text-white/60">
                      + ₹{Number(rate).toLocaleString('en-IN')} / hr ({ATTACHMENT_OPTIONS.find((o) => o.id === id)?.label || id})
                    </p>
                  ) : null
                ))}
                <p className="text-xs text-white/45 mt-1">
                  Platform fee 15% per booking applies on gross amount
                </p>
              </div>
            )}

            {/* Phase 10 — Payment Terms (credit period / early discount / late fee) */}
            <div className="mt-4 border-t border-white/10 pt-4">
              <button
                type="button"
                onClick={() => setPaymentTermsOpen((open) => !open)}
                className="flex w-full items-center justify-between text-left"
              >
                <p className="m-0 text-sm font-semibold text-white/90">Payment Terms</p>
                <span className="text-xs text-[#C9A84C]">
                  {paymentTermsOpen ? 'Hide' : 'Show'}
                </span>
              </button>
              <p className="mt-1 text-xs text-white/45">
                Credit period and incentives for contract RA-bill payments (MSMED Act).
              </p>

              {paymentTermsOpen && (
                <div className="mt-3 space-y-3">
                  <div>
                    <Label className="mb-1 block normal-case text-white/70">
                      Payment due within (days after invoice)
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="7"
                        max="45"
                        className="w-32"
                        value={regData.payment_due_days ?? 15}
                        onChange={(e) => {
                          const raw = e.target.value;
                          if (raw === '') {
                            setRegData((prev) => ({ ...prev, payment_due_days: null }));
                            return;
                          }
                          const n = Number(raw);
                          setRegData((prev) => ({
                            ...prev,
                            payment_due_days: Number.isFinite(n)
                              ? Math.max(7, Math.min(45, n))
                              : 15,
                          }));
                        }}
                      />
                      <span className="text-xs text-white/50">days</span>
                    </div>
                    <p className="mt-1 text-xs text-white/45">
                      Client must pay RA-bill within this period (MSMED Act: max 45 days)
                    </p>
                  </div>

                  <div>
                    <Label className="mb-1 block normal-case text-white/70">
                      Early payment discount (%)
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="0"
                        max="5"
                        step="0.1"
                        className="w-32"
                        value={regData.early_payment_discount_pct ?? 0}
                        onChange={(e) => {
                          const raw = e.target.value;
                          if (raw === '') {
                            setRegData((prev) => ({ ...prev, early_payment_discount_pct: 0 }));
                            return;
                          }
                          const n = Number(raw);
                          setRegData((prev) => ({
                            ...prev,
                            early_payment_discount_pct: Number.isFinite(n)
                              ? Math.max(0, Math.min(5, n))
                              : 0,
                          }));
                        }}
                      />
                      <span className="text-xs text-white/50">%</span>
                    </div>
                    <p className="mt-1 text-xs text-white/45">
                      Discount if client pays within early payment window
                    </p>
                  </div>

                  <div>
                    <Label className="mb-1 block normal-case text-white/70">
                      Early payment window (days)
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="1"
                        max="7"
                        className="w-32"
                        value={regData.early_payment_days ?? 3}
                        onChange={(e) => {
                          const raw = e.target.value;
                          if (raw === '') {
                            setRegData((prev) => ({ ...prev, early_payment_days: null }));
                            return;
                          }
                          const n = Number(raw);
                          setRegData((prev) => ({
                            ...prev,
                            early_payment_days: Number.isFinite(n)
                              ? Math.max(1, Math.min(7, n))
                              : 3,
                          }));
                        }}
                      />
                      <span className="text-xs text-white/50">days</span>
                    </div>
                  </div>

                  <div>
                    <Label className="mb-1 block normal-case text-white/70">
                      Late payment fee (% per month)
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="0"
                        max="2"
                        step="0.1"
                        className="w-32"
                        value={regData.late_payment_fee_pct ?? 0}
                        onChange={(e) => {
                          const raw = e.target.value;
                          if (raw === '') {
                            setRegData((prev) => ({ ...prev, late_payment_fee_pct: 0 }));
                            return;
                          }
                          const n = Number(raw);
                          setRegData((prev) => ({
                            ...prev,
                            late_payment_fee_pct: Number.isFinite(n)
                              ? Math.max(0, Math.min(2, n))
                              : 0,
                          }));
                        }}
                      />
                      <span className="text-xs text-white/50">%</span>
                    </div>
                    <p className="mt-1 text-xs text-white/45">
                      Applied if client pays after due date (max 2% — legal limit)
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
          </div>
        )}
      </div>

      <div style={premiumCardStyle}>
        <div
          onClick={() => toggleCard('purchase')}
          style={{ cursor: isMobile ? 'pointer' : 'default', display: 'flex',
            justifyContent: 'space-between', alignItems: 'center',
            marginBottom: isCardOpen('purchase') ? 0 : 0 }}
        >
          <p style={{ ...premiumCardTitleStyle, marginBottom: 0, borderBottom: 'none' }}>
            Purchase Details
            <span style={{ color: '#4a6080', fontWeight: 400, textTransform: 'none', letterSpacing: 'normal', marginLeft: '6px' }}>(optional)</span>
          </p>
          {isMobile && (
            <span style={{ color: '#C9A84C', fontSize: 12 }}>
              {isCardOpen('purchase') ? '▼' : '▶'}
            </span>
          )}
        </div>
        {isCardOpen('purchase') && (
          <div style={{ borderTop: '1px solid #1e3a5f', marginTop: 10, paddingTop: 10 }}>
          {/* Machine Purchase Details — optional financial section */}
          <div className="space-y-4">
            <div>
              <p className="text-sm font-semibold mb-1 text-white/90">{t('machineFinancialSection')}</p>
            </div>

            <div>
              <Label className="mb-2 block normal-case text-white/70">
                {t('machineOwnershipType')}
              </Label>
              <div className="space-y-2">
                {OWNERSHIP_OPTIONS.map((opt) => (
                  <label
                    key={opt.value}
                    className="flex items-center gap-2 text-sm cursor-pointer text-white/75"
                  >
                    <input
                      type="radio"
                      name="ownership_type"
                      value={opt.value}
                      checked={regData.ownership_type === opt.value}
                      onChange={() => setRegData((prev) => ({
                        ...prev,
                        ownership_type: opt.value,
                      }))}
                    />
                    {t(opt.labelKey)}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="reg-purchase_price" className="mb-2 block normal-case text-white/70">
                {t('machinePurchasePrice')}
              </Label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-white/60">₹</span>
                <Input
                  id="reg-purchase_price"
                  type="number"
                  min="0"
                  placeholder="Optional"
                  className="flex-1"
                  value={regData.purchase_price ?? ''}
                  onChange={(e) => setRegData((prev) => ({
                    ...prev,
                    purchase_price: e.target.value ? Number(e.target.value) : null,
                  }))}
                />
              </div>
              <p className="text-xs text-white/45 mt-1">{t('machinePurchasePriceHint')}</p>
            </div>

            {(regData.ownership_type === 'new' || regData.ownership_type === 'used_first_owner') && (
              <div>
                <Label htmlFor="reg-purchase_date" className="mb-2 block normal-case text-white/70">
                  {t('machinePurchaseDate')}
                </Label>
                <Input
                  id="reg-purchase_date"
                  type="date"
                  value={regData.purchase_date || ''}
                  onChange={(e) => setRegData((prev) => ({
                    ...prev,
                    purchase_date: e.target.value || null,
                  }))}
                />
              </div>
            )}

            <div>
              <Label htmlFor="reg-purchase_year" className="mb-2 block normal-case text-white/70">
                {t('machineYearOfManufacture')}
              </Label>
              <Input
                id="reg-purchase_year"
                type="number"
                min="1950"
                max="2100"
                placeholder="e.g. 2020"
                className="w-32"
                value={regData.purchase_year ?? ''}
                onChange={(e) => setRegData((prev) => ({
                  ...prev,
                  purchase_year: e.target.value ? Number(e.target.value) : null,
                }))}
              />
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm cursor-pointer text-white/75">
                <input
                  type="checkbox"
                  checked={loanSectionOpen}
                  onChange={(e) => {
                    setLoanSectionOpen(e.target.checked);
                    if (!e.target.checked) {
                      setRegData((prev) => ({
                        ...prev,
                        loan_amount: null,
                        monthly_emi: null,
                        loan_tenure_months: null,
                        loan_start_date: null,
                      }));
                    }
                  }}
                />
                {t('machineLoanSection')}
              </label>

              {loanSectionOpen && (
                <div className="mt-3 space-y-3 pl-1">
                  {[
                    { key: 'loan_amount', labelKey: 'machineLoanAmount' },
                    { key: 'monthly_emi', labelKey: 'machineLoanEmi' },
                    { key: 'loan_tenure_months', labelKey: 'machineLoanTenure', integer: true },
                  ].map((field) => (
                    <div key={field.key}>
                      <Label className="mb-1 block normal-case text-white/70">
                        {t(field.labelKey)}
                      </Label>
                      <div className="flex items-center gap-2">
                        {(field.key === 'loan_amount' || field.key === 'monthly_emi') && (
                          <span className="text-sm text-white/60">₹</span>
                        )}
                        <Input
                          type="number"
                          min="0"
                          placeholder="Optional"
                          className="flex-1"
                          value={regData[field.key] ?? ''}
                          onChange={(e) => setRegData((prev) => ({
                            ...prev,
                            [field.key]: e.target.value
                              ? (field.integer ? Number(e.target.value) : Number(e.target.value))
                              : null,
                          }))}
                        />
                      </div>
                    </div>
                  ))}
                  <div>
                    <Label htmlFor="reg-loan_start_date" className="mb-1 block normal-case text-white/70">
                      {t('machineLoanStartDate')}
                    </Label>
                    <Input
                      id="reg-loan_start_date"
                      type="date"
                      value={regData.loan_start_date || ''}
                      onChange={(e) => setRegData((prev) => ({
                        ...prev,
                        loan_start_date: e.target.value || null,
                      }))}
                    />
                  </div>
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="reg-fuel_consumption" className="mb-2 block normal-case text-white/70">
                {t('machineFuelConsumption')}
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="reg-fuel_consumption"
                  type="number"
                  min="0"
                  step="0.1"
                  placeholder="Optional"
                  className="w-32"
                  value={regData.fuel_consumption_liters_per_hour ?? ''}
                  onChange={(e) => setRegData((prev) => ({
                    ...prev,
                    fuel_consumption_liters_per_hour: e.target.value
                      ? Number(e.target.value) : null,
                  }))}
                />
                <span className="text-xs text-white/50">L/hr</span>
              </div>
            </div>
          </div>
          </div>
        )}
      </div>
        </>
      )}
    </div>
  );
});

export default MachineRegistrationStep2;
